import EmptyState from '@/components/EmptyState';
import { usePrintJobs, useReprintJob } from '@/features/print/hooks/usePrint';
import { copyText } from '@/lib/clipboard';
import type { PrintJob } from '@restaurant/api-client';
import {
  Actions,
  ActionsButton,
  ActionsGroup,
  ActionsLabel,
  Badge,
  Block,
  BlockFooter,
  Button,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  Segmented,
  f7,
} from 'framework7-react';
import { useState } from 'react';
import { useT } from '@/i18n';

type StatusFilter = PrintJob['status'] | 'ALL';

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

function PrintJobsPage() {
  const t = useT();
  const FILTERS: { key: StatusFilter; label: string }[] = [
    { key: 'ALL', label: t.print.jobStatusAll },
    { key: 'QUEUED', label: t.print.jobStatusQueued },
    { key: 'PRINTED', label: t.print.jobStatusPrinted },
    { key: 'FAILED', label: t.print.jobStatusFailed },
  ];
  const STATUS_META: Record<PrintJob['status'], { label: string; color: string }> = {
    QUEUED: { label: t.print.jobStatusQueued, color: 'gray' },
    PRINTING: { label: t.print.jobStatusPrinting, color: 'blue' },
    PRINTED: { label: t.print.jobStatusPrinted, color: 'green' },
    FAILED: { label: t.print.jobStatusFailed, color: 'red' },
  };
  const [filter, setFilter] = useState<StatusFilter>('ALL');
  const {
    data: jobs,
    isLoading,
    error,
  } = usePrintJobs({
    status: filter === 'ALL' ? undefined : filter,
  });
  const reprint = useReprintJob();
  // The job whose action sheet is open; null closes it.
  const [selected, setSelected] = useState<PrintJob | null>(null);

  const jobTitle = (job: PrintJob) =>
    job.payload.title ??
    (job.type === 'RECEIPT' ? t.print.receiptLabel : t.print.kitchenTicketLabel);

  const handleReprint = (job: PrintJob) => {
    reprint.mutate(job.id, { onSuccess: () => toast(t.print.reprintQueued) });
  };

  // Copy the raw ePOS XML (payload.body) so it can be pasted into an external
  // viewer to preview what the printer rendered.
  const handleCopy = async (job: PrintJob) => {
    await copyText(job.payload.body);
    toast(t.print.contentCopied);
  };

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <Page name="print-jobs">
      <Navbar title={t.print.historyTitle} backLink={t.back} />

      <Block>
        <Segmented strong>
          {FILTERS.map((f) => (
            <Button key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label}
            </Button>
          ))}
        </Segmented>
      </Block>

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title={t.print.couldntLoadJobs}
          description={(error as Error).message}
        />
      )}

      {jobs && jobs.length === 0 && (
        <EmptyState icon="doc_text" title={t.print.noJobsTitle} description={t.print.noJobsDesc} />
      )}

      {jobs && jobs.length > 0 && (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
            {jobs.map((job) => {
              const meta = STATUS_META[job.status];
              const subtitleParts = [job.printerName, fmtTime(job.queuedAt)];
              if (job.status === 'FAILED' && job.lastErrorText)
                subtitleParts.push(job.lastErrorText);
              return (
                <ListItem
                  key={job.id}
                  link="#"
                  onClick={() => setSelected(job)}
                  title={jobTitle(job)}
                  subtitle={subtitleParts.join(' · ')}
                >
                  <i slot="media" className="f7-icons" style={{ color: '#8e8e93' }}>
                    {job.type === 'RECEIPT' ? 'doc_plaintext' : 'printer'}
                  </i>
                  <div slot="after" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {job.attempts > 1 && (
                      <span style={{ color: '#8e8e93', fontSize: '13px' }}>×{job.attempts}</span>
                    )}
                    <Badge color={meta.color}>{meta.label}</Badge>
                  </div>
                </ListItem>
              );
            })}
          </List>
          <BlockFooter>{t.print.reprintFooter}</BlockFooter>
        </>
      )}

      <Actions opened={selected !== null} onActionsClosed={() => setSelected(null)}>
        <ActionsGroup>
          <ActionsLabel>{selected ? jobTitle(selected) : ''}</ActionsLabel>
          <ActionsButton
            strong
            onClick={() => {
              if (selected) handleReprint(selected);
            }}
          >
            {t.print.reprintButton}
          </ActionsButton>
          <ActionsButton
            onClick={() => {
              if (selected) void handleCopy(selected);
            }}
          >
            {t.print.copyContent}
          </ActionsButton>
        </ActionsGroup>
        <ActionsGroup>
          <ActionsButton color="gray">{t.cancel}</ActionsButton>
        </ActionsGroup>
      </Actions>
    </Page>
  );
}

export default PrintJobsPage;
